import plugin from 'tailwindcss/plugin'
import type { PluginAPI } from 'tailwindcss/types/config'

/**
 * Type scale, ported from Prism (the 2.0 design system).
 *
 * Three layers, each emitted as CSS by `typographyPlugin`:
 *   0  primitives  eight size / leading pairs, tracking keyed to size, weights
 *   1  roles       which step each family sits on (`--ui-md-size`, `--h2-leading`, …)
 *   2  classes     one `type-*` utility per text style
 *
 * Text sits at 18 and below, headings at 20 and above, so no size belongs to
 * two families. UI and code keep pixel leading because they align to rows;
 * prose holds a unitless 1.5 so nested text at another size recomputes its
 * own leading. Every heading leading is size + 6.
 *
 *   UI / Code       Prose           Headings
 *   12 / 16  xs     12 / 1.5  xs    22 / 28   h3
 *   13 / 18  sm     13 / 1.5  sm    28 / 34   h2
 *   14 / 20  md     14 / 1.5  md    36 / 42   h1
 *   18 / 26  lg     18 / 1.5  lg    48 / 54   display
 *
 * Weight is not part of a role: compose `font-medium` / `font-semibold` on
 * top. Headings default to semibold but a weight utility still wins because
 * it lands in the utilities layer.
 *
 * Display is the only step in the brand face (Greed Standard, `font-brand`);
 * the change of face, not weight, is what sets it apart from h1. It falls
 * back to Inter where the face isn't loaded.
 *
 * Sizes are authored in px and emitted in rem against a 16px root so the
 * scale reads as 14/20 in Figma and still honours a raised browser default.
 */

export const FONT_SIZES = [12, 13, 14, 18, 22, 28, 36, 48] as const
export type FontSize = (typeof FONT_SIZES)[number]

export const LINE_HEIGHTS = [16, 18, 20, 26, 28, 34, 42, 54] as const
export type LineHeight = (typeof LINE_HEIGHTS)[number]

/** Inter's dynamic metrics: -0.0223 + 0.185e^(-0.1745s), s in px, rounded to 0.001em. */
export const TRACKING: Record<FontSize, string> = {
  12: '0.001em',
  13: '-0.003em',
  14: '-0.006em',
  18: '-0.014em',
  22: '-0.018em',
  28: '-0.021em',
  36: '-0.022em',
  48: '-0.022em',
}

export const FONT_WEIGHTS = { regular: 400, medium: 500, semibold: 600, bold: 700 } as const
export type FontWeight = keyof typeof FONT_WEIGHTS

export const PROSE_LEADING = 1.5

/** Greed's ss06 swaps in the traditional question mark, matching the landing site. */
export const BRAND_FONT_FEATURES = '"ss06" 1'

export type TypeFamily = 'ui' | 'prose' | 'code' | 'heading'
export type TypeStep = 'xs' | 'sm' | 'md' | 'lg'

export interface TypeStyle {
  /** Role name and CSS variable stem, e.g. `ui-md` → `--ui-md-size`. */
  name: string
  /** Tailwind utility that applies the whole style. */
  utility: string
  family: TypeFamily
  size: FontSize
  /** Pixel leading, or the unitless prose ratio. */
  leading: LineHeight | typeof PROSE_LEADING
  tracking: string
  /** Only headings carry a default weight; text roles inherit. */
  weight?: FontWeight
  /** Set only on display, which is the one step in the brand face. */
  face?: 'brand'
  description: string
}

const rem = (px: number) => `${px / 16}rem`

const textRole = (
  family: 'ui' | 'prose' | 'code',
  step: TypeStep,
  size: FontSize,
  leading: LineHeight,
  description: string,
): TypeStyle => ({
  name: `${family}-${step}`,
  utility: `type-${family}-${step}`,
  family,
  size,
  leading: family === 'prose' ? PROSE_LEADING : leading,
  tracking: family === 'code' ? '0em' : TRACKING[size],
  description,
})

const headingRole = (
  name: string,
  size: FontSize,
  leading: LineHeight,
  weight: FontWeight,
  description: string,
  face?: 'brand',
): TypeStyle => ({
  name,
  utility: `type-${name}`,
  family: 'heading',
  size,
  leading,
  tracking: TRACKING[size],
  weight,
  description,
  ...(face ? { face } : {}),
})

export const TYPE_STYLES: ReadonlyArray<TypeStyle> = [
  textRole('ui', 'xs', 12, 16, 'Captions, timestamps, badges'),
  textRole('ui', 'sm', 13, 18, 'Default control and body size: labels, list rows, menu items'),
  textRole('ui', 'md', 14, 20, 'Roomier controls and body text'),
  textRole('ui', 'lg', 18, 26, 'Prominent controls, dialog titles'),
  textRole('prose', 'xs', 12, 16, 'Fine print'),
  textRole('prose', 'sm', 13, 18, 'Dense reading: transcripts, side panels'),
  textRole('prose', 'md', 14, 20, 'Body copy, chat messages, notes'),
  textRole('prose', 'lg', 18, 26, 'Lead paragraphs'),
  textRole('code', 'xs', 12, 16, 'Inline code inside sm and xs text'),
  textRole('code', 'sm', 13, 18, 'Code blocks, inline code in md text'),
  textRole('code', 'md', 14, 20, 'Editors'),
  textRole('code', 'lg', 18, 26, 'Presentation code'),
  headingRole('h3', 22, 28, 'semibold', 'Section headings'),
  headingRole('h2', 28, 34, 'semibold', 'Page section titles'),
  headingRole('h1', 36, 42, 'semibold', 'Page titles'),
  headingRole('display', 48, 54, 'semibold', 'Hero and empty-state statements, in the brand face', 'brand'),
]

/**
 * Tailwind `theme.fontSize`: Prism's text band and nothing else, named as Prism names it. `text-md` is
 * the 14px step and `text-sm` the 13px default, there is no `text-base`, and headings are `type-h3` to `type-display`, not
 * `text-xl` and up. Each step carries the UI role's leading and tracking, so `text-sm` is `type-ui-sm`
 * as a utility: it sits in the utilities layer and tailwind-merge resolves it against a primitive's
 * own `text-*`, which a `type-*` class cannot do.
 * Values are literal rather than `var(--font-size-*)` so apps that inherit the theme but replace `plugins` still resolve.
 */
const TEXT_STEPS = ['xs', 'sm', 'md', 'lg'] as const

export const fontSize = Object.fromEntries(
  TEXT_STEPS.map((step, index) => {
    const size = FONT_SIZES[index]
    return [step, [rem(size), { lineHeight: rem(LINE_HEIGHTS[index]), letterSpacing: TRACKING[size] }]]
  }),
) as Record<(typeof TEXT_STEPS)[number], [string, { lineHeight: string; letterSpacing: string }]>

function primitiveVars(): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const size of FONT_SIZES) vars[`--font-size-${size}`] = rem(size)
  for (const leading of LINE_HEIGHTS) vars[`--line-height-${leading}`] = rem(leading)
  for (const size of FONT_SIZES) vars[`--tracking-${size}`] = TRACKING[size]
  for (const [name, weight] of Object.entries(FONT_WEIGHTS)) vars[`--font-weight-${name}`] = String(weight)
  vars['--prose-leading'] = String(PROSE_LEADING)
  return vars
}

function roleVars(): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const style of TYPE_STYLES) {
    vars[`--${style.name}-size`] = `var(--font-size-${style.size})`
    vars[`--${style.name}-leading`] =
      style.leading === PROSE_LEADING ? 'var(--prose-leading)' : `var(--line-height-${style.leading})`
    vars[`--${style.name}-tracking`] = style.family === 'code' ? '0em' : `var(--tracking-${style.size})`
  }
  return vars
}

const FAMILY_RULES: Record<TypeFamily, Record<string, string>> = {
  // Tabular figures so numeric columns hold their width.
  ui: { fontVariantNumeric: 'tabular-nums' },
  prose: { textWrap: 'pretty' },
  code: { fontVariantNumeric: 'tabular-nums', fontVariantLigatures: 'none' },
  heading: { textWrap: 'balance' },
}

/**
 * The inline-code size that pairs with each text step: one step down, not a ratio, so code never
 * lands between steps. 18 → 14, 14 → 13, 13 → 12, and 12 keeps its own size because nothing sits
 * below it. Headings and code styles do not set one; they inherit the size of the text around them.
 */
const INLINE_CODE_SIZE: Partial<Record<FontSize, FontSize>> = { 18: 14, 14: 13, 13: 12, 12: 12 }

export const typographyPlugin = plugin(function (this: void, { addBase, addComponents, theme }: PluginAPI) {
  addBase({
    ':root': { ...primitiveVars(), ...roleVars() },
    body: {
      fontSize: 'var(--prose-sm-size)',
      lineHeight: 'var(--prose-leading)',
      letterSpacing: 'var(--prose-sm-tracking)',
      '--inline-code-size': 'var(--font-size-12)',
    },
  })

  const components: Record<string, Record<string, string>> = {}
  for (const style of TYPE_STYLES) {
    components[`.${style.utility}`] = {
      ...FAMILY_RULES[style.family],
      ...(style.family === 'code' ? { fontFamily: theme('fontFamily.mono') } : {}),
      ...(style.face === 'brand'
        ? { fontFamily: theme('fontFamily.brand'), fontFeatureSettings: BRAND_FONT_FEATURES }
        : {}),
      fontSize: `var(--${style.name}-size)`,
      lineHeight: `var(--${style.name}-leading)`,
      letterSpacing: `var(--${style.name}-tracking)`,
      ...(style.weight ? { fontWeight: `var(--font-weight-${style.weight})` } : {}),
      ...(style.family !== 'heading' && style.family !== 'code' && INLINE_CODE_SIZE[style.size]
        ? { '--inline-code-size': `var(--font-size-${INLINE_CODE_SIZE[style.size]})` }
        : {}),
    }
  }
  addComponents(components)
})
